import { useEffect, useState } from "react";
import { useCountryConfig, useOperator } from "@/hooks";
import { Button, CheckboxInput, Drawer, Icon, IconButton, Popover, RadioInput, TextInput, Typography } from "@/components/atoms";
import { getApiHostUrl } from "@/helpers/apiHelpers";
import { BOOKING_STATUS_GROUPS, BookingStatusGroup, GROUP_DISPLAY } from "../../statusGroups";
import { clsx } from "@/utils";

export type BookingHistoryFilterState = {
  passenger?: string;
  state?: string[];
  /**
   * Client-facing booking stage. Single-select: the four are the stages of one booking, so picking
   * two would be asking for a booking that is both Completed and Cancelled.
   */
  status?: BookingStatusGroup | "";
};

type StateOption = { name: string; value: string };

const useStates = () => {
  const [states, setStates] = useState<StateOption[]>([]);

  useEffect(() => {
    fetch(`${getApiHostUrl()}/v3/country/AU/state`)
      .then((res) => res.json())
      .then((data) => {
        if (data?._embedded?.state) {
          const mapped = data._embedded.state.map((s: { abbreviation: string; name: string }) => ({
            name: `${s.name} (${s.abbreviation})`,
            value: s.abbreviation,
          }));
          setStates(mapped);
        }
      })
      .catch(() => {});
  }, []);

  return states;
};

const pillClass = {
  active: "border-primary-light bg-primary-light enabled:border-primary-light active:border-primary-light",
  inactive:
    "border-neutral-gray bg-neutral-gray text-neutral-dark-gray enabled:border-neutral-gray active:border-neutral-gray hover:!border-neutral-gray hover:!bg-neutral-gray hover:!text-neutral-dark-gray",
};

interface BookingHistoryFiltersProps {
  filters: BookingHistoryFilterState;
  onFilterChange: (filters: BookingHistoryFilterState) => void;
}

export const BookingHistoryFilters = ({ filters, onFilterChange }: BookingHistoryFiltersProps) => {
  return <FilterPicker filters={filters} onFilterChange={onFilterChange} />;
};

export const ActiveFilterPills = ({ filters, onFilterChange }: BookingHistoryFiltersProps) => {
  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);

  if (!hasActiveFilters) return null;

  return (
    <div className="-mt-2 flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {filters.status !== undefined && (
          <StatusFilterPill
            value={filters.status}
            onChange={(val) => onFilterChange({ ...filters, status: val })}
            onClose={() => onFilterChange({ ...filters, status: undefined })}
          />
        )}
        {filters.passenger !== undefined && (
          <PassengerFilterPill
            value={filters.passenger}
            onChange={(val) => onFilterChange({ ...filters, passenger: val })}
            onClose={() => onFilterChange({ ...filters, passenger: undefined })}
          />
        )}
        {filters.state !== undefined && (
          <StateFilterPill
            value={filters.state ?? []}
            onChange={(val) => onFilterChange({ ...filters, state: val })}
            onClose={() => onFilterChange({ ...filters, state: undefined })}
          />
        )}
      </div>
      <Button variant="secondary" size="sm" onClick={() => onFilterChange({})}>
        Clear
      </Button>
    </div>
  );
};

const FilterPicker = ({ filters, onFilterChange }: BookingHistoryFiltersProps) => {
  const numActive = Object.values(filters).filter((v) => v !== undefined).length;

  const toggleFilter = (key: string) => {
    if (filters[key as keyof BookingHistoryFilterState] === undefined) {
      onFilterChange({ ...filters, [key]: key === "state" ? [] : "" });
      setTimeout(
        () => document.querySelectorAll(`[data-filter-name="${key}"]`).forEach((btn) => (btn as HTMLElement).click()),
        100
      );
    } else {
      onFilterChange({ ...filters, [key]: undefined });
    }
  };

  return (
    <Popover placement="bottom-end">
      <Button variant="tertiary" startIcon="Sort" size="sm">
        Filters{numActive > 0 ? ` (${numActive})` : ""}
      </Button>
      <div className="flex w-[225px] flex-col-reverse overflow-hidden rounded-lg bg-white shadow-dropdown">
        <nav className="flex flex-col">
          <div className="flex list-none flex-col">
            <FilterNavButton onClick={() => toggleFilter("status")} isActive={filters.status !== undefined}>
              Status
            </FilterNavButton>
            <FilterNavButton onClick={() => toggleFilter("passenger")} isActive={filters.passenger !== undefined}>
              Passenger
            </FilterNavButton>
            <FilterNavButton onClick={() => toggleFilter("state")} isActive={filters.state !== undefined}>
              State
            </FilterNavButton>
          </div>
        </nav>
      </div>
    </Popover>
  );
};

const FilterNavButton = ({ onClick, isActive, children }: { onClick: () => void; isActive: boolean; children: React.ReactNode }) => (
  <button className="flex items-center gap-3 px-4 py-3 hover:bg-primary-light" onClick={onClick}>
    <div className="flex-1 text-left">
      <Typography>{children}</Typography>
    </div>
    {isActive && <Icon isCustom name="check" size="sm" className="text-success" />}
  </button>
);

/**
 * Single-select by radio, not checkboxes: the four are stages of one booking, so a booking that is
 * both Completed and Cancelled is not a thing anyone can ask for.
 *
 * Labels come from GROUP_DISPLAY — the same source the status column badges from — so the filter
 * and the column can never call the same stage two different things.
 */
const StatusFilterPill = ({
  value,
  onChange,
  onClose,
}: {
  value: BookingStatusGroup | "";
  onChange: (val: BookingStatusGroup) => void;
  onClose: () => void;
}) => {
  const state = value ? "active" : "inactive";

  return (
    <Popover placement="bottom-start">
      <Button
        data-filter-name="status"
        variant="secondary"
        className={clsx(
          "h-[26px] items-center gap-0.5 rounded-full !px-2 text-primary !outline-none transition-colors [&>span]:!text-sm",
          "aria-expanded:!border-primary aria-expanded:!bg-primary-light",
          pillClass[state]
        )}
        endIcon={<Icon onClick={onClose} name="close" isCustom size="sm" className="ml-1 rounded-full hover:bg-primary-light" />}
        size="xs"
      >
        Status{value ? `: ${GROUP_DISPLAY[value].label}` : ""}
      </Button>
      <div className="flex w-[320px] flex-col rounded-lg bg-white p-4 shadow-dropdown">
        <Typography className="mb-4">Status</Typography>
        <div className="flex flex-col gap-3">
          {BOOKING_STATUS_GROUPS.map((group) => (
            <RadioInput
              key={group}
              name="booking-status-filter"
              label={GROUP_DISPLAY[group].label}
              value={group}
              checked={value === group}
              onChange={() => onChange(group)}
            />
          ))}
        </div>
      </div>
    </Popover>
  );
};

const PassengerFilterPill = ({
  value,
  onChange,
  onClose,
}: {
  value?: string;
  onChange: (val: string) => void;
  onClose: () => void;
}) => {
  const state = value ? "active" : "inactive";

  return (
    <Popover placement="bottom-start">
      <Button
        data-filter-name="passenger"
        variant="secondary"
        className={clsx(
          "h-[26px] items-center gap-0.5 rounded-full !px-2 text-primary !outline-none transition-colors [&>span]:!text-sm",
          "aria-expanded:!border-primary aria-expanded:!bg-primary-light",
          pillClass[state]
        )}
        endIcon={<Icon onClick={onClose} name="close" isCustom size="sm" className="ml-1 rounded-full hover:bg-primary-light" />}
        size="xs"
      >
        Passenger{value ? `: ${value}` : ""}
      </Button>
      <div className="flex w-[320px] flex-col rounded-lg bg-white p-4 shadow-dropdown">
        <Typography className="mb-4">Search Passenger</Typography>
        <TextInput
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          size="md"
          iconName="SearchNormal1"
          placeholder="Search Passenger"
        />
      </div>
    </Popover>
  );
};

const StateFilterPill = ({
  value,
  onChange,
  onClose,
}: {
  value: string[];
  onChange: (val: string[]) => void;
  onClose: () => void;
}) => {
  const { country } = useOperator();
  const { config } = useCountryConfig(country);
  const states = useStates();
  const displayValue = value.length > 0 ? value.join(", ") : undefined;
  const state = displayValue ? "active" : "inactive";
  const isAllSelected = states.length > 0 && value.length === states.length;

  return (
    <Popover placement="bottom-start">
      <Button
        data-filter-name="state"
        variant="secondary"
        className={clsx(
          "h-[26px] items-center gap-0.5 rounded-full !px-2 text-primary !outline-none transition-colors [&>span]:!text-sm",
          "aria-expanded:!border-primary aria-expanded:!bg-primary-light",
          pillClass[state]
        )}
        endIcon={<Icon onClick={onClose} name="close" isCustom size="sm" className="ml-1 rounded-full hover:bg-primary-light" />}
        size="xs"
      >
        State{displayValue ? `: ${displayValue}` : ""}
      </Button>
      <div className="flex max-h-[500px] w-[320px] flex-col overflow-y-auto rounded-lg bg-white p-4 shadow-dropdown">
        <Typography className="mb-4">{config.regionLabel}</Typography>
        <CheckboxInput
          label="All States"
          onChange={(e) => {
            if (e.target.checked) {
              onChange(states.map((s) => s.value));
            } else {
              onChange([]);
            }
          }}
          variant="check"
          checked={isAllSelected}
        />
        <div className="mt-4 grid gap-4">
          {states.map((s) => (
            <CheckboxInput
              key={s.value}
              label={s.name}
              value={s.value}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange([...value, e.target.value]);
                } else {
                  onChange(value.filter((v) => v !== e.target.value));
                }
              }}
              variant="check"
              checked={value.includes(s.value)}
            />
          ))}
        </div>
      </div>
    </Popover>
  );
};

/** Mobile filter drawer — triggered by icon button in mobile header */
export const BookingHistoryFilterDrawer = ({ filters, onFilterChange }: BookingHistoryFiltersProps) => {
  const { country } = useOperator();
  const { config } = useCountryConfig(country);
  const states = useStates();
  const [activeFilter, setActiveFilter] = useState<"passenger" | "state" | null>(null);
  const [open, setOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setActiveFilter(null);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <IconButton iconName="Sort" variant="secondary" size="sm" />
      <div className="flex flex-col gap-4 px-4 pb-8 pt-2">
        {!activeFilter && (
          <>
            <Typography variant="title">Select Tab</Typography>
            <div className="flex flex-col gap-2.5 pt-2">
              <button
                className="flex w-full items-center justify-between rounded-lg border-none bg-neutral-surface-gray px-4 py-3"
                onClick={() => setActiveFilter("passenger")}
              >
                <Typography variant="action">Passenger</Typography>
              </button>
              <button
                className="flex w-full items-center justify-between rounded-lg border-none bg-neutral-surface-gray px-4 py-3"
                onClick={() => setActiveFilter("state")}
              >
                <Typography variant="action">{config.regionLabel}</Typography>
              </button>
            </div>
            <div className="mt-4 flex flex-col gap-2.5">
              {(filters.passenger !== undefined || (filters.state && filters.state.length > 0)) && (
                <Button variant="tertiary" size="lg" className="w-full" onClick={() => onFilterChange({})}>
                  Clear Filters
                </Button>
              )}
              <Drawer.Close asChild>
                <Button variant="tertiary" size="lg" className="w-full">
                  Close
                </Button>
              </Drawer.Close>
            </div>
          </>
        )}

        {activeFilter === "passenger" && (
          <>
            <Typography variant="title">Passenger</Typography>
            <TextInput
              value={filters.passenger ?? ""}
              onChange={(e) => onFilterChange({ ...filters, passenger: e.target.value || undefined })}
              size="md"
              iconName="SearchNormal1"
              placeholder="Search Passenger"
            />
            <div className="mt-6 flex flex-col gap-2.5">
              <Drawer.Close asChild>
                <Button variant="primary" size="lg" className="w-full">
                  Filter Result
                </Button>
              </Drawer.Close>
              <Drawer.Close asChild>
                <Button variant="tertiary" size="md" className="w-full text-primary">
                  Close
                </Button>
              </Drawer.Close>
            </div>
          </>
        )}

        {activeFilter === "state" && (
          <>
            <Typography variant="title">{config.regionLabel}</Typography>
            <div className="grid gap-[34px] pt-4">
              <CheckboxInput
                label="All States"
                onChange={(e) => {
                  if (e.target.checked) {
                    onFilterChange({ ...filters, state: states.map((s) => s.value) });
                  } else {
                    onFilterChange({ ...filters, state: undefined });
                  }
                }}
                variant="check"
                checked={states.length > 0 && (filters.state?.length ?? 0) === states.length}
              />
              {states.map((s) => (
                <CheckboxInput
                  key={s.value}
                  label={s.name}
                  value={s.value}
                  onChange={(e) => {
                    const current = filters.state ?? [];
                    const newState = e.target.checked
                      ? [...current, e.target.value]
                      : current.filter((v) => v !== e.target.value);
                    onFilterChange({ ...filters, state: newState.length > 0 ? newState : undefined });
                  }}
                  variant="check"
                  checked={filters.state?.includes(s.value) ?? false}
                />
              ))}
            </div>
            <div className="mt-6 flex flex-col gap-2.5">
              <Drawer.Close asChild>
                <Button variant="primary" size="lg" className="w-full">
                  Filter Result
                </Button>
              </Drawer.Close>
              <Drawer.Close asChild>
                <Button variant="tertiary" size="md" className="w-full text-primary">
                  Close
                </Button>
              </Drawer.Close>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};
